import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { systemValueCodeLabel } from '../../../../generic/systemValues/systemValueLabel'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { isBlank, EMPTY_DASH } from '../detailShared'

// S429 / S437 (plan «Workflow teórico · Progress real», 23/09) — LAS CARACTERÍSTICAS DE CALIDAD, en
// grupos, pintadas COMO LAS PINTA MOS (`Mos/Widgets/Inspection/InspectionReportDetail.jsx`): mismas
// columnas, mismo orden y MISMAS CLAVES de traducción, y la PREGUNTA en una segunda línea bajo cada
// característica («QUESTION: …»). Petición del PO: «que se vean como las ve MOS».
//
// La usan las dos Calidades: la de Workflow en modo PLANIFICADO (lo que hay que medir) y la de
// Progress en modo MEDIDO (lo que se midió en un informe). Lo que cambia entre los dos son las
// columnas de la medición —valor medido, nº de serie y responsable— y la frecuencia, que solo
// tiene sentido en lo planificado.
//
// ⚠️⚠️ LO QUE NO SE COPIA DE MOS: la columna «Deviation». MOS la CALCULA; aquí no se recalcula nada
// de calidad (PO, 23/09), igual que no se pinta ningún OK/FAIL de informe.

// ¿Exige nº de serie? No viaja como campo: se deriva COMO MOS, de que alguna herramienta sea
// serializable (`InspectionReportDetail.jsx:176`).
export const requiresSerialNumber = (characteristic) => (characteristic.toolReferences || []).some((tool) => tool.serializable)

// El valor MEDIDO, como lo vio quien lo introdujo en MOS:
//   · BOOLEAN — `'true'`/`'false'` son OK/NOK, con las claves de MOS. Cualquier otro texto, tal cual:
//     MOS lo convertiría en NOK, pero es lo guardado y no se fuerza.
//   · NO_RESPONSE — no espera respuesta: se dice, con la clave de MOS.
//   · DATE — la fecha, formateada.
//   · el resto (RANGE, TEXT, LIST), el valor tal cual.
const measuredValueText = (characteristic, t_, formatDate) => {
  if (characteristic.dataType === 'NO_RESPONSE') return t_('No response required')
  // Sin medir no es un dato que falte en un campo: la fila existe y dice que está pendiente.
  if (!characteristic.measured) return t_('Not measured')
  const value = characteristic.measurement.value
  if (isBlank(value)) return t_('Not reported')
  if (characteristic.dataType === 'BOOLEAN' && value === 'true') return t_('OK')
  if (characteristic.dataType === 'BOOLEAN' && value === 'false') return t_('NOK')
  if (characteristic.dataType === 'DATE') return formatDate(value)
  return String(value)
}

const groupTitle = (group) => {
  if (!isBlank(group.code) && !isBlank(group.name)) return `${group.code} · ${group.name}`
  return group.name || group.code || ''
}

/**
 * @param {{featureGroups: Array, mode: 'planned'|'measured', testIdPrefix: string}} props
 */
const QualityFeatureGroups = ({ featureGroups = [], mode = 'planned', testIdPrefix }) => {
  const { t_ } = useSmartTranslation()
  const { formatDate } = useDateFormatter()
  const measured = mode === 'measured'
  const notReported = t_('Not reported')
  // Columnas en el ORDEN de MOS. «Measured value» y «Responsible» son de la medición; «Frequency», de
  // lo planificado.
  const columns = [
    'Order', 'Feature code', 'Feature name', 'Type', 'Nominal', 'Upper tolerance', 'Lower tolerance',
    ...(measured ? ['Measured value'] : []), 'Unit', 'Inspection Tool', 'Serial Number', 'Inspection point',
    ...(measured ? ['Responsible'] : ['Frequency'])
  ]
  const dim = { color: 'text.disabled' }

  return (
    <Stack spacing={2} data-testid={`${testIdPrefix}-groups`}>
      {featureGroups.map((featureGroup, index) => (
        <Box key={index} data-testid={`${testIdPrefix}-group-${index}`}>
          {/* Un grupo SIN GRUPO se pinta como un bloque sin título, no se esconde. */}
          {featureGroup.hasGroup && (
            <Box sx={{ mb: 0.5 }}>
              <Typography data-testid='quality-group-title' sx={{ fontWeight: 600, lineHeight: 1.2 }}>{groupTitle(featureGroup.group)}</Typography>
              {!isBlank(featureGroup.group.description) && (
                <Typography data-testid='quality-group-description' variant='caption' color='text.secondary' sx={{ display: 'block' }}>{featureGroup.group.description}</Typography>
              )}
            </Box>
          )}
          {/* 13 columns do not fit: the table scrolls on its own and never widens the block around it. */}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {columns.map((key) => <TableCell key={key}>{t_(key)}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {featureGroup.characteristics.map((c) => {
                  const isRange = c.dataType === 'RANGE'
                  const serial = requiresSerialNumber(c)
                  const tools = c.toolReferences.map((tool) => tool.name || tool.code).filter((name) => !isBlank(name))
                  return (
                    <React.Fragment key={c.featureId}>
                      <TableRow data-testid={`quality-feature-${c.featureId}`} data-measured={measured ? String(c.measured) : undefined} sx={!isBlank(c.qualitativeAttribute) ? { '& > td': { borderBottom: 'none' } } : undefined}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{isBlank(c.featureOrder) ? EMPTY_DASH : c.featureOrder}</TableCell>
                        <TableCell data-testid='feature-code' sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{isBlank(c.code) ? EMPTY_DASH : c.code}</TableCell>
                        <TableCell data-testid='feature-name'>{isBlank(c.name) ? notReported : c.name}</TableCell>
                        <TableCell data-testid='feature-type'>{isBlank(c.featureTypeName) ? (systemValueCodeLabel(t_, 'data_type', c.dataType) || EMPTY_DASH) : c.featureTypeName}</TableCell>
                        <TableCell data-testid='feature-nominal' sx={{ fontFamily: 'monospace' }}>{isBlank(c.value) ? EMPTY_DASH : c.value}</TableCell>
                        {/* Tolerancias SOLO en RANGE, como MOS: en otro tipo no aplican. */}
                        <TableCell data-testid='feature-upper-tolerance' sx={{ fontFamily: 'monospace', ...(isRange ? {} : dim) }}>{isRange && !isBlank(c.upperTolerance) ? c.upperTolerance : EMPTY_DASH}</TableCell>
                        <TableCell data-testid='feature-lower-tolerance' sx={{ fontFamily: 'monospace', ...(isRange ? {} : dim) }}>{isRange && !isBlank(c.lowerTolerance) ? c.lowerTolerance : EMPTY_DASH}</TableCell>
                        {measured && (
                          <TableCell data-testid='feature-measured-value' sx={{ fontFamily: 'monospace', fontWeight: 700, ...(c.measured || c.dataType === 'NO_RESPONSE' ? {} : { color: 'text.secondary', fontWeight: 400 }) }}>
                            {measuredValueText(c, t_, formatDate)}
                          </TableCell>
                        )}
                        <TableCell data-testid='feature-unit'>{isBlank(c.unitCode) ? EMPTY_DASH : c.unitCode}</TableCell>
                        <TableCell data-testid='feature-tools'>{tools.length === 0 ? EMPTY_DASH : tools.join(' · ')}</TableCell>
                        {/* El nº de serie solo cuando la herramienta lo exige; si no, no aplica. */}
                        <TableCell data-testid='feature-serial' sx={{ fontFamily: 'monospace', ...(serial ? {} : dim) }}>
                          {!serial
                            ? EMPTY_DASH
                            : measured
                              ? (c.measured ? (c.measurement.serialNumber || notReported) : EMPTY_DASH)
                              : t_('Required')}
                        </TableCell>
                        <TableCell data-testid='feature-inspection-point'>{isBlank(c.inspectionPoint.name) ? EMPTY_DASH : c.inspectionPoint.name}</TableCell>
                        {measured
                          ? <TableCell data-testid='feature-responsible'>{c.measured ? (c.measurement.responsible || notReported) : EMPTY_DASH}</TableCell>
                          : <TableCell data-testid='feature-frequency' sx={{ fontFamily: 'monospace' }}>{isBlank(c.frequency) ? EMPTY_DASH : c.frequency}</TableCell>}
                      </TableRow>
                      {/* ⭐ LA PREGUNTA, en una segunda línea y como la pinta MOS: «QUESTION: …» en versalitas
                          y color primario. Un «OK» sin la pregunta que responde no dice nada. */}
                      {!isBlank(c.qualitativeAttribute) && (
                        <TableRow data-testid={`quality-feature-${c.featureId}-question`}>
                          <TableCell colSpan={columns.length} sx={{ pt: 0, pb: '0.75rem' }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', pl: 1, whiteSpace: 'normal' }}>
                              <Typography sx={{ color: 'primary.main', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>
                                {t_('Question')}:
                              </Typography>
                              <Typography data-testid='feature-question' sx={{ lineHeight: 1.4 }}>{c.qualitativeAttribute}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Stack>
  )
}

export default QualityFeatureGroups
